<template>
    <section class="z-20 lg:relative md-max:relative">
      <!-- Desktop background image -->
      <nuxt-img
        class="absolute z-10 hidden object-cover object-top w-full h-full lg:block"
        sizes="xs:330px sm:440px md:760px lg:1024px xl:1200x 2xl:1400px"
        loading="lazy"
        format="webp"
        width="1920"
        height="1080"
        :src="desktopBackgroundImage"
        :alt="desktopBackgroundAlt"
      />
  
      <!-- Mobile background image -->
      <nuxt-img
        class="absolute z-10 object-cover object-center w-full h-full lg:hidden"
        sizes="xs:330px sm:440px md:760px lg:1024px xl:1200x 2xl:1400px"
        loading="lazy"
        format="webp"
        width="1080"
        height="1920"
        :src="mobileBackgroundImage"
        :alt="mobileBackgroundAlt"
      />
  
      <div class="relative z-20 px-4 py-8 mx-auto max-w-screen-2xl lg:py-40 2xl:px-0">
        <div class="grid grid-cols-12">
          <div class="col-span-12 lg:col-span-7 lg:col-start-5 xl:col-span-7 xl:col-start-5">
            <div class="text-4xl text-center text-white sm:text-5xl 2xl:text-6xl font-ww-regular" v-html="firstText"></div>
            <div class="text-6xl text-center text-white sm:text-7xl 2xl:text-8xl font-ww-medium" v-html="secondText"></div>
            <div class="text-center text-white 2xl:text-6xl text-4xl sm:text-5xl sm:!leading-[50px] font-ww-regular xl:px-28" v-html="thirdText"></div>
  
            <div class="grid grid-cols-12 pb-[100%] md:pb-[50%] mt-6 sm:mt-12 lg:0 xl:px-20 gap-x-4 md:gap-x-8 lg:pb-0">
              <div v-for="(icon, index) in icons" :key="index" class="flex flex-col h-full col-span-4 sm:grid gap-y-1 sm:gap-y-2">
                <div>
                  <nuxt-img
                    class="mx-auto lg:w-full w-52"
                    sizes="xs:110px sm:130px md:208px lg:210px xl:210px 2xl:210px"
                    loading="lazy"
                    format="webp"
                    width="224"
                    height="224"
                    :src="icon.imageSrc"
                    :alt="icon.imageAlt"
                  />
                </div>
                <div class="leading-5 text-center text-white textbase xl:text-xl sm:text-lg font-ww-medium" v-html="icon.title"></div>
                <div class="text-base italic leading-5 text-center text-white xl:text-lg" v-html="icon.description"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  const props = defineProps({
    desktopBackgroundImage: {
      type: String,
      default: '/assets/images/COGNITION_-_SECTION_2_-_BACKGROUND.png',
    },
    desktopBackgroundAlt: {
      type: String,
      default: 'background section',
    },
    mobileBackgroundImage: {
      type: String,
      default: '/assets/images/COGNITION_-_SECTION_2_-_BACKGROUND-mobile.png',
    },
    mobileBackgroundAlt: {
      type: String,
      default: 'background section mobile',
    },
    firstText: {
      type: String,
      default: 'With extracts',
    },
    secondText: {
      type: String,
      default: '8X MORE POTENT',
    },
    thirdText: {
      type: String,
      default: 'than any other brand on the market',
    },
    icons: {
      type: Array,
      default: () => [
        {
          imageSrc: '/assets/images/COGNITION_-_SECTION_2_-_ICON_1_CHAGA.png',
          imageAlt: 'Icon 1 - Organic Lions Mane',
          title: 'ORGANIC <br>LIONS MANE',
          description: 'Support <br>cognitive function',
        },
        {
          imageSrc: '/assets/images/COGNITION_-_SECTION_2_-_ICON_2_LIONS_MANE.png',
          imageAlt: 'Icon 2 - Organic Chaga',
          title: 'ORGANIC <br>CHAGA',
          description: 'Support <br>immunity',
        },
        {
          imageSrc: '/assets/images/COGNITION_-_SECTION_2_-_ICON_3_COFFEE.png',
          imageAlt: 'Icon 3 - Lifeboost Organic Coffee',
          title: 'LIFEBOOST <br>ORGANIC COFFEE',
          description: 'Low-acid <br>and delicious',
        },
      ],
    },
  });
  
  defineBuilderComponent({
    friendlyName: "BackgroundTitleListImages",
    inputs: [
      {
        name: "desktopBackgroundImage",
        type: "file",
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        defaultValue: "/assets/images/COGNITION_-_SECTION_2_-_BACKGROUND.png",
      },
      {
        name: "desktopBackgroundAlt",
        type: "string",
        defaultValue: "background section",
      },
      {
        name: "mobileBackgroundImage",
        type: "file",
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        defaultValue: "/assets/images/COGNITION_-_SECTION_2_-_BACKGROUND-mobile.png",
      },
      {
        name: "mobileBackgroundAlt",
        type: "string",
        defaultValue: "background section mobile",
      },
      {
        name: "firstText",
        type: "richText",
        defaultValue: 'With extracts',
      },
      {
        name: "secondText",
        type: "richText",
        defaultValue: '8X MORE POTENT',
      },
      {
        name: "thirdText",
        type: "richText",
        defaultValue: 'than any other brand on the market',
      },
      {
        name: "icons",
        type: "list",
        defaultValue: [
          {
            imageSrc: '/assets/images/COGNITION_-_SECTION_2_-_ICON_1_CHAGA.png',
            imageAlt: 'Icon 1 - Organic Lions Mane',
            title: 'ORGANIC <br>LIONS MANE',
            description: 'Support <br>cognitive function',
          },
          {
            imageSrc: '/assets/images/COGNITION_-_SECTION_2_-_ICON_2_LIONS_MANE.png',
            imageAlt: 'Icon 2 - Organic Chaga',
            title: 'ORGANIC <br>CHAGA',
            description: 'Support <br>immunity',
          },
          {
            imageSrc: '/assets/images/COGNITION_-_SECTION_2_-_ICON_3_COFFEE.png',
            imageAlt: 'Icon 3 - Lifeboost Organic Coffee',
            title: 'LIFEBOOST <br>ORGANIC COFFEE',
            description: 'Low-acid <br>and delicious',
          },
        ],
        subFields: [
          {
            name: "imageSrc",
            type: "file",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
            defaultValue: "/assets/images/COGNITION_-_SECTION_2_-_ICON_1_CHAGA.png",
          },
          {
            name: "imageAlt",
            type: "string",
            defaultValue: "Icon 1 - Organic Lions Mane",
          },
          {
            name: "title",
            type: "richText",
            defaultValue: "ORGANIC <br>LIONS MANE",
          },
          {
            name: "description",
            type: "richText",
            defaultValue: "Support <br>cognitive function",
          },
        ],
      },
    ],
  });
  </script>
  